import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import './Toast.less';

class Toast extends PureComponent {
    static propTypes = {
        /** Content of the Toast */
        content: PropTypes.node.isRequired,
        /** Delay in ms to close the toast. If set to false, the toast needs to be closed manually */
        autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        /** Dismiss toast on click */
        closeOnClick: PropTypes.bool,
        /** Event listener to fire when the toast is clicked */
        onClick: PropTypes.func,
        /** Event listener to fire when the toast has been dismissed (whether manual or via autoClose) */
        onClose: PropTypes.func,
        /** Event listener to fire when the toast has been manually dismissed */
        onCloseClick: PropTypes.func,
        /** Type of Toast (default, inverse) to display */
        type: PropTypes.oneOf(['default', 'inverse']),
        /** (internal) source: redux - id generated during action creation for programmatic removal */
        id: PropTypes.number.isRequired,
        /** (internal) source: redux - remove toast programmatically */
        remove: PropTypes.bool,
        /** (internal) source: redux - action for removing the toast from state once removed from the UI */
        removeToastFromState: PropTypes.func
    }

    static defaultProps = {
        autoClose: 5000,
        closeOnClick: true,
        onClick: () => {},
        onClose: () => {},
        onCloseClick: () => {},
        removeToastFromState: () => {},
        remove: false,
        type: 'default'
    }

    /* istanbul ignore next */
    constructor(props) {
        super(props);

        // timeouts for triggering css animations
        this.timeouts = [];

        // state for managing css animations
        this.state = {
            isAdded: false,
            isClosed: false,
            isRemoved: false
        };
    }

    componentDidMount() {
        const {autoClose} = this.props;
        // auto close
        if (autoClose && typeof autoClose === 'number') {
            this.timeouts.push(setTimeout(() => {
                this.closeAuto();
            }, autoClose));
        }
    }

    componentDidUpdate(prevProps) {
        this.closeProgrammatically(prevProps);
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        /* istanbul ignore next */
        this.clearTimeouts();
    }

    add = () => {
        if (this.state.isAdded) {
            return;
        }
        this.setState({isAdded: true});
    }

    /* istanbul ignore next */
    clearTimeouts = () => {
        /* istanbul ignore next */
        this.timeouts.forEach((timeout) => {
            clearTimeout(timeout);
        });
        this.timeouts = [];
    }

    close = () => {
        /* istanbul ignore next */
        if (this.state.isClosed) {
            return;
        }

        // clear the auto close timeout since we're already closing
        this.clearTimeouts();

        // animate out
        this.setState({isClosed: true});
        this.remove();
    }

    closeAuto = () => {
        const {isClosed} = this.state;
        if (!isClosed) {
            this.close();
        }
    }

    closeManually = () => {
        this.close();
    }

    closeProgrammatically = (prevProps = {}) => {
        const {remove} = this.props;
        if (remove && remove !== prevProps.remove) {
            this.close();
        }
    }

    handleCloseClick = (e) => {
        const {id, onCloseClick} = this.props;
        e.preventDefault();
        e.stopPropagation();
        onCloseClick(id);
        this.closeManually();
    }

    handleToastClick = (e) => {
        const {closeOnClick, id, onClick} = this.props;
        e.preventDefault();
        e.stopPropagation();
        onClick(id);

        /* istanbul ignore else */
        if (closeOnClick) {
            this.closeManually();
        }
    }

    remove = () => {
        const {id, onClose, removeToastFromState} = this.props;

        // animate removal
        this.timeouts.push(setTimeout(() => {
            this.setState({isRemoved: true});
        }, 250));

        // remove toast from state
        // all animations take 500ms, so giving removal animations time to complete
        this.timeouts.push(setTimeout(() => {
            onClose(id);
            removeToastFromState(id);
        }, 750));
    }

    render() {
        const {isAdded, isClosed, isRemoved} = this.state;
        const {closeOnClick, content, type} = this.props;
        const toastClasses = classNames({
            'toast': true,
            'toast--is-added': isAdded,
            'toast--is-closed': isClosed,
            'toast--is-closeable': closeOnClick,
            'toast--is-removed': isRemoved,
            'toast--inverse': type === 'inverse'
        });

        // animate in
        this.timeouts.push(setTimeout(this.add, 250));

        return (
            <div
                className={toastClasses}
                onClick={this.handleToastClick}
                onKeyDown={this.handleToastClick}
                role="button"
                tabIndex={0}
            >
                <button
                    className="btn btn-link btn-sm toast__close"
                    onClick={this.handleCloseClick}
                    type="button"
                >
                    <svg className="toast__svg inline-svg">
                        <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#toast-x" />
                    </svg>
                </button>
                <div className="toast__content" role="alert">
                    {content}
                </div>
            </div>
        );
    }
}

export default Toast;